import React, { useEffect, useState } from "react";
import { toastError, toastSuccess } from "../../utils";
import * as actions from "./actions";
import * as commonActions from "../common/actions";
import {
  Grid,
  Box,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";

const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 64px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  textAlign: 'left',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: "'DM Sans', sans-serif",
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
  },
});

const ProfileHeaderCard = styled(Paper)({
  borderRadius: '20px',
  padding: '40px',
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  boxShadow: '0 8px 24px rgba(26, 115, 232, 0.25)',
  marginBottom: '32px',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-5%',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.08)',
  },
});

const AvatarCircle = styled(Box)({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  '& svg': {
    fontSize: '50px',
    color: 'white',
  },
});

const InfoCard = styled(Paper)({
  borderRadius: '16px',
  padding: '28px',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  marginBottom: '20px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
});

const QuickInfoCard = styled(Paper)({
  borderRadius: '16px',
  padding: '24px',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
});

const SectionTitle = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#202124',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '& svg': {
    color: '#1a73e8',
  },
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const InfoLabel = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#5f6368',
  minWidth: '140px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const InfoValue = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1rem',
  fontWeight: 500,
  color: '#202124',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const EditButton = styled(IconButton)({
  color: '#1a73e8',
  padding: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#e3f2fd',
    transform: 'scale(1.1)',
  },
});

const ActionButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
  },
  '&:disabled': {
    background: '#e0e0e0',
    color: '#9e9e9e',
    boxShadow: 'none',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: '8px',
    maxWidth: '500px',
    width: '100%',
    backgroundColor: '#fafafa',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#202124',
  padding: '24px 24px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#fafafa',
});

const StyledDialogContent = styled(DialogContent)({
  padding: '24px',
  background: '#fafafa',
});

const StyledDialogActions = styled(DialogActions)({
  padding: '16px 24px 24px',
  gap: '12px',
  background: '#fafafa',
});

const StyledTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    fontFamily: "'DM Sans', sans-serif",
    '& fieldset': {
      borderColor: '#e0e0e0',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: '#1a73e8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1a73e8',
    },
  },
  '& .MuiInputBase-input': {
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600,
    color: '#5f6368',
    '&.Mui-focused': {
      color: '#1a73e8',
    },
  },
});

const StyledTextarea = styled('textarea')({
  width: '100%',
  padding: '12px 16px',
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1rem',
  borderRadius: '12px',
  border: '2px solid #e0e0e0',
  backgroundColor: '#ffffff',
  color: '#202124',
  resize: 'vertical',
  minHeight: '120px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#1a73e8',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#1a73e8',
    boxShadow: '0 0 0 3px rgba(26, 115, 232, 0.1)',
  },
});

const FieldLabel = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#202124',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
});

const NoteText = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  color: '#5f6368',
  textAlign: 'center',
  marginTop: '16px',
  '& a': {
    color: '#1a73e8',
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

export default function Profile(props) {
  const [user, setUser] = useState({});
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState("phone");
  const [changeField, setChangeField] = useState("");
  const [password, setPassword] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    getAllUserData();

    // Track user activity on profile page
    commonActions.trackUserActivity({
      page: '/profile',
      timestamp: new Date().toISOString()
    }).catch((err) => {
      console.error('Error tracking activity:', err);
    });
  }, []);

  const getAllUserData = () => {
    props.setLoader(true);
    actions
      .getUser()
      .then((res) => {
        props.setLoader(false);
        setUser(res.body);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const setOpenModal = (type, value) => {
    if (type === "phone" || type === "address") {
      setChangeField(value);
    }
    setModalType(type);
    setOpen(true);
  };

  const onSubmitChange = (type) => {
    let sendObj = {};
    if (type === "phone") {
      sendObj = {
        phone: changeField,
      };
      if (changeField.toString().length < 10) {
        return toastError("Invalid phone number");
      }
    } else if (type === "address") {
      sendObj = {
        address: changeField,
      };
    } else {
      sendObj = password;
      if (password.password.length < 8) {
        return toastError("Password must be 8 characters long.");
      }
      if (password.password !== password.confirmPassword) {
        return toastError("Password doesn't match");
      }
    }
    props.setLoader(true);
    actions
      .updateUser(sendObj)
      .then((res) => {
        props.setLoader(false);
        setOpen(false);
        toastSuccess(res.message);
        getAllUserData();
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const onHandleClose = () => {
    setChangeField("");
    setPassword({
      password: "",
      confirmPassword: "",
    });
    setOpen(false);
  };

  const {
    address = "",
    email = "",
    name = "",
    phone = "",
    user_id = "",
  } = user || {};

  return (
    <PageContainer>
      <SEO
        title="My Profile"
        description="Manage your account information and settings."
      />
      <Fade in timeout={800}>
        <Box>
          <PageHeader>
            <Typography variant="h1">
              <PersonIcon sx={{ fontSize: 36, color: '#1a73e8' }} />
              My Profile
            </Typography>
          </PageHeader>

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={10} lg={8}>
              {/* Profile Header Card */}
              <ProfileHeaderCard elevation={0}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <AvatarCircle>
                    <AccountCircleIcon />
                  </AvatarCircle>
                  <Typography sx={{
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    mb: 0.5,
                  }}>
                    {name || 'User'}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.875rem',
                    opacity: 0.9,
                    fontFamily: "'DM Sans', sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <BadgeIcon sx={{ fontSize: 18 }} />
                    Client Code: FR-IN-{user_id}
                  </Typography>
                </Box>
              </ProfileHeaderCard>

              {/* Quick Info Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <QuickInfoCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <EmailIcon sx={{ color: '#1a73e8', fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: '0.75rem',
                          color: '#5f6368',
                          fontFamily: "'DM Sans', sans-serif",
                          mb: 0.5,
                          fontWeight: 600,
                        }}>
                          Email Address
                        </Typography>
                        <Typography sx={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: '#202124',
                          fontFamily: "'DM Sans', sans-serif",
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {email}
                        </Typography>
                      </Box>
                    </Box>
                  </QuickInfoCard>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <QuickInfoCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <PhoneIcon sx={{ color: '#00bfa5', fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                          fontSize: '0.75rem',
                          color: '#5f6368',
                          fontFamily: "'DM Sans', sans-serif",
                          mb: 0.5,
                          fontWeight: 600,
                        }}>
                          Phone Number
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: '#202124',
                            fontFamily: "'DM Sans', sans-serif",
                          }}>
                            {phone}
                          </Typography>
                          <EditButton size="small" onClick={() => setOpenModal("phone", phone)}>
                            <EditIcon fontSize="small" />
                          </EditButton>
                        </Box>
                      </Box>
                    </Box>
                  </QuickInfoCard>
                </Grid>
              </Grid>

              {/* Details Card */}
              <InfoCard>
                <SectionTitle>
                  <PersonIcon />
                  Account Details
                </SectionTitle>

                <InfoRow>
                  <InfoLabel>
                    <HomeIcon sx={{ fontSize: 18 }} />
                    Address
                  </InfoLabel>
                  <InfoValue>
                    {address || 'Not provided'}
                    <EditButton size="small" onClick={() => setOpenModal("address", address)}>
                      <EditIcon fontSize="small" />
                    </EditButton>
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>
                    <LockIcon sx={{ fontSize: 18 }} />
                    Password
                  </InfoLabel>
                  <InfoValue>
                    •••••••
                    <EditButton size="small" onClick={() => setOpenModal("password")}>
                      <EditIcon fontSize="small" />
                    </EditButton>
                  </InfoValue>
                </InfoRow>

                <NoteText>
                  To change your name or email, please create a <Link to="/tickets">support ticket</Link>.
                </NoteText>
              </InfoCard>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Update Phone/Address/Password Modal */}
      <StyledDialog
        open={open}
        onClose={onHandleClose}
        disableScrollLock
      >
        <StyledDialogTitle>
          {modalType === "phone" ? "Update Phone Number" : modalType === "address" ? "Update Address" : "Change Password"}
          <IconButton
            onClick={onHandleClose}
            sx={{
              color: '#5f6368',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {modalType !== "password" ? (
            <Box>
              <FieldLabel>
                {modalType === "phone" ? "Phone Number" : "Address"}
              </FieldLabel>
              {modalType === "phone" ? (
                <StyledTextField
                  type="number"
                  placeholder="Enter phone number"
                  variant="outlined"
                  size="small"
                  value={changeField}
                  onChange={(e) => {
                    if (e.target.value.toString().length > 10) {
                      return;
                    }
                    setChangeField(e.target.value);
                  }}
                  fullWidth
                />
              ) : (
                <StyledTextarea
                  placeholder="Enter your address"
                  value={changeField}
                  onChange={(e) => setChangeField(e.target.value)}
                />
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 3 }}>
                <FieldLabel>New Password</FieldLabel>
                <StyledTextField
                  type="password"
                  placeholder="Enter new password"
                  variant="outlined"
                  size="small"
                  value={password.password}
                  onChange={(e) =>
                    setPassword({ ...password, password: e.target.value })
                  }
                  fullWidth
                />
              </Box>
              <Box>
                <FieldLabel>Confirm Password</FieldLabel>
                <StyledTextField
                  type="password"
                  placeholder="Confirm new password"
                  variant="outlined"
                  size="small"
                  value={password.confirmPassword}
                  onChange={(e) =>
                    setPassword({
                      ...password,
                      confirmPassword: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Box>
            </Box>
          )}
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            onClick={onHandleClose}
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              color: '#5f6368',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() =>
              onSubmitChange(
                modalType === "phone" ? "phone" : modalType === "address" ? "address" : "password"
              )
            }
            sx={{ boxShadow: 'none' }}
          >
            Update
          </ActionButton>
        </StyledDialogActions>
      </StyledDialog>
    </PageContainer>
  );
}
